import React from 'react'
import PropTypes from 'prop-types'
import MuiButton from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'

const SIZE_SX = {
  sm: { height: '36px', fontSize: '13px', padding: '0 12px', borderRadius: 'var(--radius-md)' },
  default: { height: '48px', fontSize: '15px', padding: '0 20px', borderRadius: 'var(--radius-md)' },
  lg: { height: '52px', fontSize: '15px', padding: '0 20px', borderRadius: 'var(--radius-md)' },
}

const VARIANT_PROPS = {
  primary: { variant: 'contained', disableElevation: true },
  danger: { variant: 'contained', color: 'error', disableElevation: true },
  secondary: { variant: 'outlined' },
  ghost: { variant: 'outlined' },
  text: { variant: 'text' },
}

const VARIANT_SX = {
  primary: {
    backgroundColor: 'var(--color-brand)',
    '&:hover': {
      backgroundColor: 'var(--color-brand-strong)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  // Not MUI's augmentColor-derived hover shade — it can't decompose a
  // var() string. color-mix() keeps the hover 100% token-driven instead.
  danger: {
    '&:hover': {
      backgroundColor: 'color-mix(in srgb, var(--vo-danger) 85%, black)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  secondary: {
    borderColor: 'var(--border-default)',
    color: 'var(--color-brand)',
    '&:hover': {
      borderColor: 'var(--border-default)',
      backgroundColor: 'var(--vo-mist)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  ghost: {
    borderColor: 'var(--border-default)',
    color: 'var(--color-brand)',
    '&:hover': {
      borderColor: 'var(--border-default)',
      backgroundColor: 'var(--vo-mist)',
      boxShadow: 'none',
    },
  },
  text: {
    color: 'var(--color-brand)',
    '&:hover': {
      backgroundColor: 'var(--vo-mist)',
      boxShadow: 'none',
    },
  },
}

export const Button = ({
  variant,
  size,
  disabled,
  isLoading,
  startIcon,
  endIcon,
  children,
  sx,
  ...otherProps
}) => {
  const isDisabled = disabled || isLoading

  return (
    <MuiButton
      {...VARIANT_PROPS[variant]}
      disabled={isDisabled}
      startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : startIcon}
      endIcon={endIcon}
      sx={[{ ...SIZE_SX[size], ...VARIANT_SX[variant] }, sx]}
      {...otherProps}
    >
      {children}
    </MuiButton>
  )
}

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger', 'text']),
  size: PropTypes.oneOf(['sm', 'default', 'lg']),
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

Button.defaultProps = {
  variant: 'primary',
  size: 'default',
  disabled: false,
  isLoading: false,
  sx: {},
}
